import { useMemo, useContext } from 'react';
import type { INavigationData } from '../../../../dotcms/dotcms-models/dotcms-navigation';
import type { IFormattedNavigation } from '../types';
import NavigationMenuContext from '../navigation-menu-context';
import { useGetPageConditionRequirementResults } from '../../../../app/pages-requiring-condition';
import { Page } from '../../../../app/page-url-path';
import { useGetRouteToIssuerPage } from '../../../../app/get-route-to-page';

const formatNavigation = (
  navigationMenu: INavigationData,
  pageOptions: {
    pageRoute: string;
    isPageAvailable: boolean;
    iconClassName?: string;
  }[],
  isCashlessPageAvailable: boolean,
): IFormattedNavigation => {
  const {
    resourceLearnMoreTitle: resourcesLearnAndEarnMoreTitle,
    resourceLearnMoreComment: resourcesLearnAndEarnMoreText,
    cashlessParticipationCategory,
    resourcesDataCategory,
    viewCategoryTitle,
  } = navigationMenu;

  const resourcesLinksGroupTitle =
    resourcesDataCategory && resourcesDataCategory[0]?.name;
  const resourcesLinksGroup =
    resourcesDataCategory && resourcesDataCategory[0]?.navigationItems;
  const filteredResourcesLinks =
    resourcesLinksGroup
      ?.map((link) => {
        const pageOption = pageOptions.find(
          (x) => x.pageRoute.toLowerCase() === link.path.toLowerCase(),
        );
        return {
          ...link,
          pageOption,
          icon: pageOption?.iconClassName ?? link.icon ?? '',
        };
      })
      .filter((x) => x.pageOption?.isPageAvailable) ?? [];

  const isSecondSectionNewLabel =
    cashlessParticipationCategory &&
    Boolean(cashlessParticipationCategory[0]?.isMarkedWithLabelNew);
  const cashlessParticipationLinkGroupTitle =
    cashlessParticipationCategory && cashlessParticipationCategory[0]?.name;
  const cashlessParticipationLinkGroup =
    cashlessParticipationCategory &&
    cashlessParticipationCategory[0]?.navigationItems;
  const cashlessParticipationLink =
    cashlessParticipationCategory &&
    cashlessParticipationCategory[0] &&
    cashlessParticipationCategory[0]?.navigationItems &&
    cashlessParticipationCategory[0]?.navigationItems[0];

  return {
    ...(filteredResourcesLinks && filteredResourcesLinks.length > 0
      ? {
        resourcesLinksGroup: filteredResourcesLinks,
        resourcesLinksGroupTitle,
        resourcesLearnAndEarnMoreTitle,
        resourcesLearnAndEarnMoreText,
      }
      : {}),
    ...(isCashlessPageAvailable
      ? {
        cashlessParticipationLink,
        cashlessParticipationLinkGroupTitle,
        cashlessParticipationLinkGroup,
        isSecondSectionNewLabel,
      }
      : {}),
    viewCategoryTitle,
  };
};

const useNavigationMenu = () => {
  const { data, loading, error } = useContext(NavigationMenuContext);
  const cashlessRoute = useGetRouteToIssuerPage(Page.ABOUT_CASHLESS);
  const pageAvailability = useGetPageConditionRequirementResults();
  const resourcesRoute = useGetRouteToIssuerPage(Page.RESOURCE_LIBRARY);
  const calculatorRoute = useGetRouteToIssuerPage(Page.CALCULATOR);
  const navigationMenu = useMemo(
    () =>
      data &&
      formatNavigation(
        data,
        [
          {
            pageRoute: cashlessRoute,
            isPageAvailable: pageAvailability[Page.ABOUT_CASHLESS],
          },
          {
            pageRoute: resourcesRoute,
            isPageAvailable: pageAvailability[Page.RESOURCE_LIBRARY],
            iconClassName: 'fa fa-book',
          },
          {
            pageRoute: calculatorRoute,
            isPageAvailable: pageAvailability[Page.CALCULATOR],
            iconClassName: 'fa fa-calculator',
          },
        ],
        pageAvailability[Page.ABOUT_CASHLESS],
      ),
    [data, cashlessRoute, pageAvailability, resourcesRoute, calculatorRoute],
  );

  return useMemo(
    () => ({
      data: navigationMenu,
      loading,
      error,
    }),
    [navigationMenu, error, loading],
  );
};

export default useNavigationMenu;
